/**
 * Typography Configuration
 * Consistent type scale for Material-UI theme
 * Updated: May 2026 - Enhanced hierarchy and readability
 * 
 * RULE: Never use fontSize prop directly
 * BAD: <Typography fontSize="0.75rem">
 * GOOD: <Typography variant="caption">
 */

import { TypographyOptions } from '@mui/material/styles/createTypography';

export const typography: TypographyOptions = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
  
  // Display & Headings
  h1: {
    fontSize: 36,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.025em',
    color: '#3C4251',
  },
  h2: {
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: '#3C4251',
  },
  h3: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.015em',
    color: '#3C4251',
  },
  h4: {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
    color: '#525866',
  },
  h5: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0',
    color: '#525866',
  },
  h6: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0',
    color: '#525866',
  },
  
  // Body Text
  body1: {
    fontSize: 15,
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0',
    color: '#6B7280',
  },
  body2: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0',
    color: '#6B7280',
  },
  
  // Small Text
  caption: {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.01em',
    color: '#9CA3AF',
  },
  overline: {
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: '#9CA3AF',
  },
  
  // Buttons & Links
  button: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.75,
    textTransform: 'none',
    letterSpacing: '0.01em',
  },
  
  // Subtitle variants
  subtitle1: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0',
    color: '#525866',
  },
  subtitle2: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.005em',
    color: '#6B7280',
  },
};

/**
 * Typography Variant Mapping Guide
 * 
 * Use these guidelines to choose the right variant:
 * 
 * Page Titles: h4 (24px)
 * Section Headers: h5 (20px) or h6 (18px)
 * Card Titles: h6 (18px) or subtitle1 (16px)
 * Body Text: body1 (16px) for main content, body2 (14px) for secondary
 * Small Text: caption (12px) for labels, timestamps, metadata
 * Tiny Text: overline (10px) for uppercase labels, badges
 * 
 * NEVER use direct fontSize - always use variants!
 */

export default typography;
